import React, {
	createContext,
	useState,
	useEffect,
	useContext,
	ReactNode,
	useCallback,
} from 'react';
import { jwtDecode } from 'jwt-decode';
import { fetchData } from '@quizzach/utils';
import { IProfileInfo, IUserToken } from '@quizzach/interfaces';

interface AuthState {
	isAuthenticated: boolean | undefined;
	profileInfo: IProfileInfo | undefined;
	token: string | null;
	loading: boolean;
	error: boolean;
}

interface AuthContextValue extends AuthState {
	setToken: (token: string | null) => void;
	setProfileInfo: (profileInfo: IProfileInfo | undefined) => void;
	refreshProfile: () => Promise<IProfileInfo | undefined>;
	handleLogout: VoidFunction;
}

const defaultAuthState: AuthState = {
	isAuthenticated: undefined,
	profileInfo: undefined,
	token: null,
	loading: true,
	error: false,
};

interface AuthProviderProps {
	children: ReactNode;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
	const [authState, setAuthState] = useState<AuthState>(defaultAuthState);

	const isTokenValid = useCallback((token: string) => {
		if (!token) return false;
		try {
			const decodedToken = jwtDecode<IUserToken>(token);
			return decodedToken?.exp && decodedToken.exp * 1000 > Date.now();
		} catch {
			return false;
		}
	}, []);

	const setToken = useCallback((token: string | null) => {
		if (token) {
			window.localStorage.setItem('userToken', token);
		} else {
			window.localStorage.removeItem('userToken');
		}

		setAuthState((prevState) => ({
			...prevState,
			token,
			isAuthenticated: !!token,
			profileInfo: token ? prevState.profileInfo : undefined,
		}));
	}, []);

	const setProfileInfo = useCallback(
		(profileInfo: IProfileInfo | undefined) => {
			setAuthState((prevState) => ({
				...prevState,
				profileInfo,
			}));
		},
		[]
	);

	const getProfile = useCallback(async (): Promise<
		IProfileInfo | undefined
	> => {
		setAuthState((prevState) => ({
			...prevState,
			loading: true,
			error: false,
		}));

		try {
			const response = await fetchData<IProfileInfo>(
				`/api/profile`,
				{ method: 'GET' },
				{ authRoute: true }
			);

			if (response.status >= 300) {
				console.error('HTTP Error:', response.status);
				setAuthState((prevState) => ({
					...prevState,
					profileInfo: undefined,
					loading: false,
					error: true,
				}));
				return undefined;
			} else {
				const nextProfileInfo = response.data || undefined;

				setAuthState((prevState) => ({
					...prevState,
					profileInfo: nextProfileInfo,
					loading: false,
					error: false,
				}));
				return nextProfileInfo;
			}
		} catch (err) {
			console.error('Network Error:', err);
			setAuthState((prevState) => ({
				...prevState,
				profileInfo: undefined,
				loading: false,
				error: true,
			}));
			return undefined;
		}
	}, []);

	const handleLogout = useCallback((): void => {
		window.localStorage.removeItem('userToken');
		setAuthState({
			isAuthenticated: false,
			profileInfo: undefined,
			token: null,
			loading: false,
			error: false,
		});
	}, []);

	// Initialize auth state on mount
	useEffect(() => {
		const storedToken = window.localStorage.getItem('userToken');

		if (storedToken && isTokenValid(storedToken)) {
			setAuthState({
				isAuthenticated: true,
				profileInfo: undefined,
				token: storedToken,
				loading: false, // Will be set to true when getProfile runs
				error: false,
			});
		} else {
			// Clean up invalid token
			if (storedToken) {
				window.localStorage.removeItem('userToken');
			}

			setAuthState({
				isAuthenticated: false,
				profileInfo: undefined,
				token: null,
				loading: false,
				error: false,
			});
		}
	}, [isTokenValid]); // Only run once on mount (isTokenValid is stable due to useCallback)

	// Fetch profile when token changes and is valid
	useEffect(() => {
		if (
			authState.token &&
			isTokenValid(authState.token) &&
			authState.isAuthenticated
		) {
			getProfile();
		}
	}, [authState.token, authState.isAuthenticated, getProfile, isTokenValid]);

	const contextValue: AuthContextValue = {
		...authState,
		setToken,
		setProfileInfo,
		refreshProfile: getProfile,
		handleLogout,
	};

	return (
		<AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
	);
};

export const useAuth = () => {
	const context = useContext(AuthContext);
	if (context === undefined) {
		throw new Error('useAuth must be used within an AuthProvider');
	}
	return context;
};
